import { isRouteErrorResponse, useRouteError } from 'react-router';
import { lazy, useState } from 'react';
import { Button } from 'components/ui/Button';

// Local Imports
import { Loadable } from 'components/shared/Loadable';

// ----------------------------------------------------------------------

const isDev = import.meta.env.DEV;

const app = {
  401: lazy(() => import('./401')),
  404: lazy(() => import('./404')),
  429: lazy(() => import('./429')),
  500: lazy(() => import('./500')),
};

// Production error view - simple and user-friendly
function ProductionErrorView() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 dark:bg-dark-900">
      <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-lg dark:bg-dark-800">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <svg
              className="h-8 w-8 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        <h1 className="mb-2 text-xl font-semibold text-gray-800 dark:text-dark-100">
          Something went wrong
        </h1>

        <p className="mb-6 text-gray-600 dark:text-dark-300">
          An unexpected error occurred. Please contact your system administrator
          if the problem persists.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button
            onClick={() => (window.location.href = '/')}
            className="bg-primary-600 text-white hover:bg-primary-700"
          >
            Return to Home
          </Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}

// Development error view - detailed for debugging
function DevelopmentErrorView({ error }) {
  const [showDetails, setShowDetails] = useState(false);

  const errorMessage = error?.message || 'An unexpected error occurred';
  const errorStack = error?.stack || '';
  const errorName = error?.name || 'Error';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-4 flex items-center text-red-600">
          <svg
            className="mr-4 h-10 w-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h1 className="text-2xl font-bold">Application Error</h1>
        </div>

        <div className="mb-6">
          <h2 className="mb-2 text-xl font-semibold">{errorName}</h2>
          <p className="text-gray-700">{errorMessage}</p>
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row">
          <Button onClick={() => window.location.reload()}>Reload Page</Button>
          <Button
            variant="outline"
            onClick={() => (window.location.href = '/')}
          >
            Go to Home
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>

        {showDetails && (
          <div className="mt-4">
            <h3 className="mb-2 text-lg font-semibold">Error Details</h3>
            <div className="max-h-96 overflow-auto rounded bg-gray-100 p-4">
              <p className="whitespace-pre-wrap font-mono text-sm">
                {errorStack}
              </p>
            </div>
            <div className="mt-4">
              <h4 className="mb-2 font-semibold">Component Tree:</h4>
              <div className="max-h-60 overflow-auto rounded bg-gray-100 p-4">
                <p className="font-mono text-sm">
                  {error?.componentStack || 'Component stack not available'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-500">
          <p>
            If this problem persists, please contact your administrator or try
            again later.
          </p>
        </div>
      </div>
    </div>
  );
}

function RootErrorBoundary() {
  const error = useRouteError();

  // Log error to console for debugging (in both dev and prod for monitoring)
  console.error('Application error:', error);

  if (isRouteErrorResponse(error)) {
    const Component = Loadable(app[error.status] || app[500]);
    return <Component error={error} />;
  }

  // For non-route errors (JavaScript errors, etc.)
  // Show simple view in production, detailed view in development
  return isDev ? (
    <DevelopmentErrorView error={error} />
  ) : (
    <ProductionErrorView />
  );
}

export default RootErrorBoundary;
